import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type { AxiosInstance } from 'axios';
import { msalConfig, loginRequest } from '../config/authConfig';

/**
 * Sign-in proof for /api/* calls (specs/2026-10-30-secure-managed-functions.md).
 *
 * The API accepts the user's MSAL ID token - it's issued to this app, so
 * the API can verify it - in X-Growth-Api-Token (SWA intercepts
 * Authorization in production).
 */

export const TOKEN_HEADER = 'X-Growth-Api-Token';

// Create MSAL instance for token acquisition
export const msalInstance = new PublicClientApplication(msalConfig);

let msalInit: Promise<void> | null = null;
export const initializeMsal = (): Promise<void> => {
  if (!msalInit) msalInit = msalInstance.initialize();
  return msalInit;
};

// Renew a little before the ID token expires, so a request never carries an expired one
const RENEW_BEFORE_MS = 5 * 60 * 1000;
let redirecting = false;

/** The signed-in user's ID token, or '' when nobody is signed in */
export const getApiToken = async (): Promise<string> => {
  await initializeMsal();
  const account = msalInstance.getAllAccounts()[0];
  if (!account) return '';
  try {
    let result = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    const exp = Number((result.idTokenClaims as any)?.exp || 0) * 1000;
    if (!result.idToken || exp - Date.now() < RENEW_BEFORE_MS) {
      result = await msalInstance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
    }
    return result.idToken || '';
  } catch (error) {
    // The session can't be renewed silently: ask the user to sign in again
    if (error instanceof InteractionRequiredAuthError && !redirecting) {
      redirecting = true;
      await msalInstance.acquireTokenRedirect({ ...loginRequest, account });
    }
    return '';
  }
};

/**
 * Attach the token to every request made through `instance`. With
 * `apiOnly`, only to relative /api/ URLs (for the shared global axios).
 */
export const installApiAuth = (instance: AxiosInstance | undefined, apiOnly = false): void => {
  instance?.interceptors?.request.use(async (config) => {
    if (apiOnly && !String(config.url || '').startsWith('/api/')) return config;
    try {
      const token = await getApiToken();
      if (token) config.headers[TOKEN_HEADER] = token;
    } catch (error) {
      console.warn('⚠️ Could not get a sign-in token for the API request:', error);
    }
    return config;
  });
};

/** Headers for fetch() calls to /api/ */
export const apiAuthHeaders = async (): Promise<Record<string, string>> => {
  const token = await getApiToken().catch(() => '');
  return token ? { [TOKEN_HEADER]: token } : {};
};
